import React, { memo, useEffect, useMemo, useState } from 'react';

import { Confirm, Modal } from '@components/common';
import ImportMapData from '@components/ModalComponents/ImportMapDataContainer/ImportMapData';
import FieldsMapping from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/FieldsMapping';
import ImportRules from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/ImportRules';
import ResultStatistic from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/ResultStatistic';
import UploadFile from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/UploadFile';

import { useImportAssets } from '@hooks/assets/useImportAssets';
import { useImportPolygons } from '@hooks/polygons/useImportPolygons';
import { useImportSites } from '@hooks/sites/useImportSites';

import { useAppDispatch } from '@store/store';

import { clearFieldsMapping } from '@slices/importMapData';

import { generateImportProps } from '@utils/import';

import { IImportAssetComponent } from '@interfaces/components/form';
import { TFileTypes } from '@interfaces/import/import';

import { EImportingDataType, TImportingDataType } from '@enums/dropzone';

import { polygonFileTypesWithoutMapping } from '@constants/common';
import { MODAL_WIDTH_580, modalCloseConfirm } from '@constants/modals';
import {
	FIELDS_MAPPING_DELETE_MODAL_WIDTH,
	FIELDS_MAPPING_MODAL_WIDTH,
	FIELDS_MAPPING_POLYGON_MODAL_WIDTH,
	IMPORT_RULES_MODAL_WIDTH,
	RESULT_STATISTIC_MODAL_WIDTH,
} from './constants';
import { componentNames } from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/constants';
import {
	assetFileErrors,
	polygonFileErrors,
	siteFileErrors,
	supportedFilesForAssetsDescription,
	supportedFilesForPolygonsDescription,
	supportedFilesForSitesDescription,
	uploadAssetsDescription,
	uploadPolygonsDescription,
	uploadSitesDescription,
} from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/UploadFile/constants';

interface IImportMapDataContainer {
	acceptedFileTypes: TFileTypes;
	importType: TImportingDataType;
	isShowImportModal: boolean;
	toggleImportModal: () => void;
	additionalParams?: {
		fieldsTechnology?: string;
	};
	resultStatisticTitle?: string;
	importVendor?: string;
}

const ImportMapDataContainer = ({
	acceptedFileTypes,
	importType,
	isShowImportModal,
	toggleImportModal,
	additionalParams,
	resultStatisticTitle = 'Skipped files',
	importVendor = 'NOKIA',
}: IImportMapDataContainer) => {
	const dispatch = useAppDispatch();

	const [fileNames, setFileNames] = useState<string[]>([]);
	const [isDeleteRule, setIsDeleteRule] = useState(false);
	const [isResultStatisticComponent, setIsResultStatisticComponent] = useState(false);
	const [isImportRulesComponent, setIsImportRulesComponent] = useState(false);
	const [isFieldsMappingComponent, setIsFieldsMappingComponent] = useState(false);

	const [assetImportIds, setAssetImportIds] = useState<string[]>([]);
	const [polygonImportIds, setPolygonImportIds] = useState<string[]>([]);
	const [siteImportIds, setSiteImportIds] = useState<string[]>([]);

	const closeModals = () => {
		toggleImportModal();
		setIsCloseConfirmModal(false);
		setCurrentComponent(components[0].value);
		setAssetImportIds([]);
		setFileNames([]);
		setPolygonImportIds([]);
		dispatch(clearFieldsMapping());
	};

	const supportedFilesDescription = importType === EImportingDataType.asset
		? supportedFilesForAssetsDescription
		: importType === EImportingDataType.site
			? supportedFilesForSitesDescription
			: supportedFilesForPolygonsDescription;

	const uploadDescription = importType === EImportingDataType.asset
		? uploadAssetsDescription
		: importType === EImportingDataType.site
			? uploadSitesDescription
			: uploadPolygonsDescription;

	const isWithoutFieldsMapping = importType === EImportingDataType.polygon
		&& polygonFileTypesWithoutMapping.some(type => fileNames?.[0]?.includes(type));

	const errors = importType === EImportingDataType.asset
		? assetFileErrors
		: importType === EImportingDataType.site
			? siteFileErrors
			: polygonFileErrors;

	const {
		allFields: allAssetFields,
		assetFieldsSamples,
		assetImportStatistic,
		assetSystemFields,
		createAssetUploadUrl,
		downloadSample: downloadAssetSampleFile,
		executeAssetImport,
		importTypes: importAssetTypes,
		isLoadingAssetFieldsMapping,
		isLoadingAssetImportRules,
		isLoadingAssetResultStatistic,
		isUploadingAssetFile,
		saveAssetFieldsMapping,
		saveImportRules: saveAssetImportRules,
		numRows: numRowsAssets,
	} = useImportAssets({
		closeModals,
		importId: assetImportIds?.[0],
		setImportIds: setAssetImportIds,
		importType,
		isResultStatisticComponent,
		isImportRulesComponent,
		isFieldsMappingComponent,
	});

	const {
		createPolygonUploadUrl,
		downloadPolygonSampleFile,
		executePolygonImport,
		isLoadingPolygonFieldsMapping,
		isLoadingPolygonResultStatistic,
		isUploadingPolygonFile,
		polygonFieldsSample,
		polygonImportStats,
		polygonSystemFields,
		savePolygonFieldsMapping,
	} = useImportPolygons({
		closeModals,
		setImportIds: setPolygonImportIds,
		importId: polygonImportIds?.[0],
		isResultStatisticComponent,
		importType,
		isFieldsMappingComponent,
	});

	const {
		downloadSample: downloadSitesSampleFile,
		importTypes: importSitesTypes,
		allFields: allSitesFields,
		saveImportRules: saveSitesImportRules,
		executeSitesImport,
		isLoadingSitesFieldsMapping,
		isLoadingSitesImportRules,
		isLoadingSitesResultStatistic,
		isUploadingSitesFile,
		saveSitesFieldsMapping,
		sitesFieldsSamples,
		sitesSystemFields,
		sitesImportStatistic,
		createSitesUploadUrl,
		numRows: numRowsSites,
	} = useImportSites({
		closeModals,
		importId: siteImportIds?.[0],
		setImportIds: setSiteImportIds,
		importType,
		fieldsTechnology: additionalParams?.fieldsTechnology,
		isResultStatisticComponent,
		isImportRulesComponent,
		isFieldsMappingComponent,
		vendor: importVendor,
	});

	const {
		createUploadUrl,
		downloadSample,
		executeImport,
		fieldsSamples,
		importIds,
		importStatistic,
		isLoadingFieldsMapping,
		isLoadingResultStatistic,
		isUploadingFile,
		saveFieldsMapping,
		setImportIds,
		systemFields,
		importTypes,
		allFields,
		saveImportRules,
		isLoadingImportRules,
		numRows,
		moduleName,
	} = useMemo(() => generateImportProps({
		downloadSitesSampleFile,
		importSitesTypes,
		allSitesFields,
		saveSitesImportRules,
		executeSitesImport,
		isLoadingSitesFieldsMapping,
		isLoadingSitesImportRules,
		isLoadingSitesResultStatistic,
		isUploadingSitesFile,
		saveSitesFieldsMapping,
		sitesFieldsSamples,
		sitesSystemFields,
		sitesImportStatistic,
		createSitesUploadUrl,
		createPolygonUploadUrl,
		downloadPolygonSampleFile,
		executePolygonImport,
		isLoadingPolygonFieldsMapping,
		isLoadingPolygonResultStatistic,
		isUploadingPolygonFile,
		polygonFieldsSample,
		polygonImportStats,
		polygonSystemFields,
		savePolygonFieldsMapping,
		allAssetFields,
		assetFieldsSamples,
		assetImportStatistic,
		assetSystemFields,
		createAssetUploadUrl,
		downloadAssetSampleFile,
		executeAssetImport,
		importAssetTypes,
		isLoadingAssetFieldsMapping,
		isLoadingAssetImportRules,
		isLoadingAssetResultStatistic,
		isUploadingAssetFile,
		saveAssetFieldsMapping,
		saveAssetImportRules,
		numRowsAssets,
		importType,
		setAssetImportIds,
		assetImportIds,
		siteImportIds,
		numRowsSites,
		setSiteImportIds,
		polygonImportIds,
		setPolygonImportIds,
	}), [
		downloadSitesSampleFile,
		importSitesTypes,
		allSitesFields,
		saveSitesImportRules,
		executeSitesImport,
		isLoadingSitesFieldsMapping,
		isLoadingSitesImportRules,
		isLoadingSitesResultStatistic,
		isUploadingSitesFile,
		saveSitesFieldsMapping,
		sitesFieldsSamples,
		sitesSystemFields,
		sitesImportStatistic,
		createSitesUploadUrl,
		createPolygonUploadUrl,
		downloadPolygonSampleFile,
		executePolygonImport,
		isLoadingPolygonFieldsMapping,
		isLoadingPolygonResultStatistic,
		isUploadingPolygonFile,
		polygonFieldsSample,
		polygonImportStats,
		polygonSystemFields,
		savePolygonFieldsMapping,
		allAssetFields,
		assetFieldsSamples,
		assetImportStatistic,
		assetSystemFields,
		createAssetUploadUrl,
		downloadAssetSampleFile,
		executeAssetImport,
		importAssetTypes,
		isLoadingAssetFieldsMapping,
		isLoadingAssetImportRules,
		isLoadingAssetResultStatistic,
		isUploadingAssetFile,
		saveAssetFieldsMapping,
		saveAssetImportRules,
		numRowsAssets,
		importType,
		setAssetImportIds,
		assetImportIds,
		siteImportIds,
		numRowsSites,
		setSiteImportIds,
		polygonImportIds,
		setPolygonImportIds,
	]);

	const components: Array<IImportAssetComponent> = useMemo(() => (
		[
			{
				value: componentNames.uploadFile,
				title: 'Upload file',
				component: UploadFile,
				width: MODAL_WIDTH_580,
				props: {
					createUploadUrl,
					setFileNames,
					downloadSample,
					setImportIds,
					isUploadingFile,
					fileNames,
					supportedFilesDescription,
					uploadDescription,
					acceptedFileTypes,
					importType,
					errors,
				},
			},
			...(importType === EImportingDataType.asset) || (importType === EImportingDataType.site)
				? [{
					value: componentNames.importRules,
					title: 'Import rules',
					component: ImportRules,
					width: IMPORT_RULES_MODAL_WIDTH,
					props: {
						fileName: fileNames?.[0],
						importTypes,
						allFields,
						saveImportRules,
						isLoadingImportRules,
						setIsDeleteRule,
						numRows,
						name: importType,
					},
				}]
				: [],
			...isWithoutFieldsMapping
				? []
				: [{
					value: componentNames.fieldsMapping,
					title: 'Fields mapping',
					component: FieldsMapping,
					width: isDeleteRule
						? FIELDS_MAPPING_DELETE_MODAL_WIDTH
						: importType === EImportingDataType.polygon
							? FIELDS_MAPPING_POLYGON_MODAL_WIDTH
							: FIELDS_MAPPING_MODAL_WIDTH,
					props: {
						fieldsSamples,
						systemFields,
						saveFieldsMapping,
						isLoadingFieldsMapping,
						isDeleteRule,
						moduleName,
					},
				}],
			{
				value: componentNames.resultStatistic,
				title: 'Result statistic',
				component: ResultStatistic,
				width: RESULT_STATISTIC_MODAL_WIDTH,
				props: {
					isViewDeleteCount: importType === EImportingDataType.asset,
					fileName: fileNames?.[0],
					importStatistic,
					executeImport,
					isLoadingResultStatistic,
					resultStatisticTitle,
				},
			},
		]
	), [
		createUploadUrl,
		downloadSample,
		setImportIds,
		isUploadingFile,
		fileNames,
		supportedFilesDescription,
		uploadDescription,
		acceptedFileTypes,
		importType,
		errors,
		importTypes,
		allFields,
		saveImportRules,
		isLoadingImportRules,
		numRows,
		isWithoutFieldsMapping,
		isDeleteRule,
		fieldsSamples,
		systemFields,
		saveFieldsMapping,
		isLoadingFieldsMapping,
		moduleName,
		importStatistic,
		executeImport,
		isLoadingResultStatistic,
		resultStatisticTitle,
	]);

	const [isCloseConfirmModal, setIsCloseConfirmModal] = useState(false);
	const [currentComponent, setCurrentComponent] = useState(() => components[0].value);

	useEffect(() => {
		setIsResultStatisticComponent(currentComponent === componentNames.resultStatistic);
		setIsImportRulesComponent(currentComponent === componentNames.importRules);
		setIsFieldsMappingComponent(currentComponent === componentNames.fieldsMapping);
	}, [currentComponent]);

	const toggleCloseConfirmModal = () => setIsCloseConfirmModal(prevState => !prevState);

	const currentComponentProps: IImportAssetComponent | undefined = useMemo(
		() => components.find(component => component.value === currentComponent),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currentComponent],
	);

	const isScrollableContent = ((currentComponentProps?.value === components[2]?.value) && !isDeleteRule)
		|| importType === EImportingDataType.polygon;

	return (
		<>
			<Modal
				isOpen={isShowImportModal}
				isScrollableContent={isScrollableContent}
				onClose={toggleCloseConfirmModal}
				title={currentComponentProps?.title}
				width={currentComponentProps?.width}
			>
				<ImportMapData
					components={components}
					currentComponent={currentComponent}
					importIds={importIds}
					setCurrentComponent={setCurrentComponent}
					toggleCloseConfirmModal={toggleCloseConfirmModal}
				/>
			</Modal>
			<Modal
				isOpen={isCloseConfirmModal}
				onClose={toggleCloseConfirmModal}
			>
				<Confirm
					onClose={toggleCloseConfirmModal}
					onSubmit={closeModals}
					{...modalCloseConfirm}
				/>
			</Modal>
		</>
	);
};

export default memo(ImportMapDataContainer);
